import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faFilePdf, faPaperclip } from '@fortawesome/pro-light-svg-icons';
import axios from 'axios';
import envConfig from '../../../../envConfig';

import {
  fetchTransactions,
  selectTransactions,
  selectTransactionsError,
  selectTransactionsLoading,
} from '../../../../redux/slices/billingSlice/billingSlice';

const TransactionsTable = () => {
  const dispatch = useDispatch();

  const reduxTransactions = useSelector(selectTransactions);
  const loading = useSelector(selectTransactionsLoading);
  const error = useSelector(selectTransactionsError);

  const [transactions, setTransactions] = useState([]);
  const [editingPO, setEditingPO] = useState(null);
  const [poValue, setPoValue] = useState('');

  useEffect(() => {
    dispatch(fetchTransactions());
  }, [dispatch]);

  useEffect(() => {
    if (reduxTransactions) {
      setTransactions(reduxTransactions);
    }
  }, [reduxTransactions]);

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp * 1000).toLocaleDateString();
  };

  const formatDateShort = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp * 1000).toLocaleDateString();
  };

  const getDescription = (tx) => {
    if (tx.adjustment) {
      if (tx.description) return tx.description;
      return '-';
    }

    if (tx.type === 'PAYMENT') {
      if (tx.accountNumber) return tx.accountNumber;
      return '-';
    }

    if (tx.type === 'CHARGE') {
      if (tx.description) return tx.description;

      if (tx.serviceStartDate && tx.serviceEndDate) {
        return (
          `${formatDateShort(tx.serviceStartDate)
          } - ${
            formatDateShort(tx.serviceEndDate)}`
        );
      }

      return 'Service charge';
    }

    return '-';
  };

  const shouldShowStatus = (tx) => {
    if (tx.type === 'PAYMENT') return false;
    if (tx.adjustment) return false;
    return true;
  };

  const getStatusContent = (tx) => {
    if (tx.paid) return 'Paid';

    if (tx.serviceStartDate) {
      return `Due ${formatDateShort(tx.serviceStartDate)}`;
    }

    return 'Due';
  };

  const startEditingPO = (tx) => {
    setEditingPO(tx.id);
    setPoValue(tx.purchaseOrderNumber ? tx.purchaseOrderNumber : '');
  };

  const savePO = async (id) => {
    try {
      const url = `${envConfig.raaUrl}/subscription/billing/data/assign-purchase-order`;

      const formData = new URLSearchParams();
      formData.append('invoiceId', id);
      formData.append('purchaseOrderNumber', poValue);

      await axios.post(url, formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      setTransactions((prev) => prev.map((tx) => {
        if (tx.id === id) {
          return { ...tx, purchaseOrderNumber: poValue };
        }
        return tx;
      }));

      setEditingPO(null);
      setPoValue('');
      // eslint-disable-next-line no-shadow
    } catch (error) {
      // eslint-disable-next-line no-alert
      alert('Failed to save purchase order number');
    }
  };

  const renderPOSection = (tx) => {
    // locked if paid
    if (tx.paid) {
      if (!tx.purchaseOrderNumber) return null;

      return (
        <span className="text-muted small">
          <FontAwesomeIcon icon={faPaperclip} className="mr-1" />
          {tx.purchaseOrderNumber}
        </span>
      );
    }

    if (editingPO === tx.id) {
      return (
        <div className="d-flex align-items-center gap-1">
          <FontAwesomeIcon icon={faPaperclip} className="text-muted" />

          <input
            className="form-control form-control-sm"
            style={{ width: '110px' }}
            value={poValue}
            onChange={(e) => setPoValue(e.target.value)}
            placeholder="PO #"
          />

          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={() => savePO(tx.id)}
          >
            <FontAwesomeIcon icon={faCheck} />
          </button>
        </div>
      );
    }

    if (tx.purchaseOrderNumber) {
      return (
        <span className="text-muted small">
          <FontAwesomeIcon icon={faPaperclip} className="mr-1" />
          {tx.purchaseOrderNumber}
        </span>
      );
    }

    return (
      <span
        className="text-muted small"
        style={{ cursor: 'pointer' }}
        onClick={() => startEditingPO(tx)}
      >
        <FontAwesomeIcon icon={faPaperclip} className="mr-1" />
        PO #
      </span>
    );
  };

  const renderInvoiceType = (tx) => {
    if (tx.adjustment) return 'Adjustment';

    if (tx.type === 'PAYMENT') {
      return 'Payment';
    }

    if (tx.type === 'CHARGE') {
      return (
        <div className="d-flex align-items-center">
          <a
            href={`${envConfig.raaUrl}/subscription/billing/invoice/${tx.id}`}
          >
            <span className="text-primary fw-semibold mr-3">Invoice</span>

            <span
              className="text-primary mr-3"
            >
              <FontAwesomeIcon icon={faFilePdf} />
            </span>
          </a>

          {renderPOSection(tx)}
        </div>
      );
    }

    return tx.type || '-';
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid mt-4">
        <div className="alert alert-danger">
          {error && error.message ? error.message : 'Failed to load transactions'}
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4">
      <div className="table-responsive">
        <table className="table table-sm table-striped align-middle">
          <thead className="table-light">
            <tr>
              <th>Date</th>
              <th>Type</th>
              <th>Description</th>
              <th>Status</th>
              <th className="text-end">Service</th>
              <th className="text-end">Tax</th>
              <th className="text-end">Total</th>
            </tr>
          </thead>
          <tbody>
            {transactions.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center text-muted py-4">
                  No Transactions
                </td>
              </tr>
            ) : (
              transactions.map((tx) => {
                let { amount } = tx;
                if (amount === null || amount === undefined) amount = 0;

                let tax = tx.taxAmount;
                if (tax === null || tax === undefined) tax = 0;

                let { total } = tx;
                if (total === null || total === undefined) {
                  total = amount + tax;
                }

                const isPaid = tx.paid === true;

                return (
                  <tr key={tx.id}>
                    <td>{formatDate(tx.date)}</td>

                    <td>{renderInvoiceType(tx)}</td>

                    <td>{getDescription(tx)}</td>

                    <td>
                      {shouldShowStatus(tx) && (
                      <span
                        className={
                          `badge ${
                            isPaid ? '' : 'bg-warning text-dark'}`
                        }
                      >
                          {getStatusContent(tx)}
                      </span>
                      )}
                    </td>

                    <td className="text-end">{amount.toFixed(2)}</td>
                    <td className="text-end">{tax.toFixed(2)}</td>
                    <td className="text-end">{total.toFixed(2)}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TransactionsTable;
